import { Link, NavLink, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    BellRingIcon,
    CheckIcon,
    ChevronRightIcon,
    SparklesIcon,
    UsersIcon,
    ZapIcon,
    CircleIcon,
} from "lucide-react";

import { Avatar } from "../../components/account/Avatar";
import { DealProgress } from "../../components/DealProgress";
import { TaskPriorityIcon } from "../../components/TaskPriorityPill";
import {
    DealStepTimeline,
    isAtOrPastStep,
} from "../../components/DealStepTimeline";
import { RecentActivity } from "../../components/activity/RecentActivity";
import Loading from "../../components/Loading";
import { classNames } from "../../utils/cn";

import {
    DealStep,
    TaskPriority,
    TaskStatus,
    DealTasksQuery,
    useDealTasksQuery,
    useSellerDashboardQuery,
    SellerDashboardQuery,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { authSelectors } from "../../store/auth/selector";


export function InternalDashboard(props: {
}) {
    const { dealId } = useParams<{ dealId: string }>();

    if (!dealId) {
        return <div>No deal selected</div>;
    }

    return <InternalDashboardContent dealId={dealId} />;
}

function InternalDashboardContent(props: { dealId: string; }) {
    const client = useGqlClient();
    const account = useSelector(authSelectors.account);
    const dashboardQuery = useSellerDashboardQuery(client, {
        dealId: props.dealId,
    });

    const dealTasksQuery = useDealTasksQuery(
        client,
        {
            filters: {
                dealID: props.dealId,
            },
        },
    );

    if (dashboardQuery.error) {
        return <div>Something went wrong</div>;
    }

    if (dashboardQuery.isPending || !dashboardQuery.data) {
        return (
            <div className="flex-1">
                <Loading />
            </div>
        );
    }

    const { deal } = dashboardQuery.data;
    const showBuyers = isAtOrPastStep(deal.dealStep, DealStep.FindingBuyers);


    return (
        <div className="h-[calc(100vh-150px)] overflow-hidden rounded-b-md flex flex-col">
            {/* Deal Step Timeline - Minimal top bar */}
            <div className="bg-white border-b border-gray-100 px-6 py-4">
                <DealStepTimeline currentStep={deal.dealStep} />
            </div>

            {/* Main Content Grid */}
            <div className="flex-1 overflow-y-auto bg-concrete-50">
                <div className="p-6 grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
                    {/* Left Column - Information Required (takes 2 cols on xl) */}
                    <div className="xl:col-span-1">
                        <InformationRequiredSection
                            dealId={props.dealId}
                            tasks={dealTasksQuery.data?.tasks ?? []}
                            isTasksLoading={dealTasksQuery.isPending}
                            currentAccountId={account?.id}
                        />
                    </div>

                    <div className="xl:col-span-1">
                        <SellerDdqlRequestSection
                            dealId={props.dealId}
                            sellerRequestList={deal.sellerRequestList}
                        />
                    </div>

                    {/* Right Column - Needs Attention & Buyers */}
                    <div className="space-y-6">
                        <NeedsAttentionSection />
                        <BuyersSection
                            dealId={props.dealId}
                            buyers={deal.buyers}
                            dealStages={deal.dealStages}
                            isActive={showBuyers}
                        />
                    </div>
                </div>

                <RecentActivity dealId={props.dealId} />
                {/* Recent Activity - Full Width at Bottom */}
            </div>
        </div>
    );
}

type SellerRequestListData = SellerDashboardQuery["deal"]["sellerRequestList"];
type SellerRequestListNode = NonNullable<SellerRequestListData>;
type SellerRequestQuestion = SellerRequestListNode["questions"][0];

type SellerRequestQuestionState = "no_answer" | "documents_found" | "answered";

function isRequestQuestionAnswered(question: SellerRequestQuestion): boolean {
    return (question.answers?.length ?? 0) > 0;
}

function hasSuggestedDocuments(question: SellerRequestQuestion): boolean {
    return (question.suggestedAnswer?.references?.length ?? 0) > 0;
}

function getSellerRequestQuestionState(question: SellerRequestQuestion): SellerRequestQuestionState {
    if (isRequestQuestionAnswered(question)) {
        return "answered";
    }
    if (hasSuggestedDocuments(question)) {
        return "documents_found";
    }
    return "no_answer";
}

function flattenSellerRequestQuestions(node: SellerRequestListNode): SellerRequestQuestion[] {
    const current = node.questions ?? [];
    const nested = (node.children ?? []).flatMap((child: SellerRequestListNode["children"][0]) =>
        // @ts-ignore
        flattenSellerRequestQuestions(child as SellerRequestListNode),
    );
    return [...current, ...nested];
}

type SellerRequestSection = {
    id: string;
    name: string;
    questions: SellerRequestQuestion[];
};

function flattenSellerRequestSections(node: SellerRequestListNode): SellerRequestSection[] {
    const currentSection: SellerRequestSection = {
        id: node.id,
        name: node.name,
        questions: node.questions ?? [],
    };
    const childSections = (node.children ?? []).flatMap(
        (child: SellerRequestListNode["children"][0]) =>
            // @ts-ignore
            flattenSellerRequestSections(child as SellerRequestListNode),
    );

    return [currentSection, ...childSections];
}

function SellerDdqlRequestSection(props: {
    dealId: string;
    sellerRequestList: SellerRequestListData;
}) {
    const { dealId, sellerRequestList } = props;

    if (!sellerRequestList) {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <SparklesIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No information list</h3>
                    <p className="text-gray-500 text-sm max-w-xs">
                        Information requests will appear here
                    </p>
                </div>
            </div>
        );
    }

    const allQuestions = flattenSellerRequestQuestions(sellerRequestList);
    const answeredCount = allQuestions.filter((q) => getSellerRequestQuestionState(q) === "answered").length;
    const documentsFoundCount = allQuestions.filter(
        (q) => getSellerRequestQuestionState(q) === "documents_found",
    ).length;
    const totalCount = allQuestions.length;
    const sections = flattenSellerRequestSections(sellerRequestList)
        .filter((section) => section.questions.length > 0);
    const answeredProgress = totalCount === 0 ? 0 : (answeredCount / totalCount) * 100;
    const docsProgress = totalCount === 0 ? 0 : (documentsFoundCount / totalCount) * 100;

    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-persian-990 to-persian-950 px-5 py-4 text-white">
                <div>
                    <h2 className="text-base font-semibold">Information Requests</h2>
                    <div className="mt-2 flex items-start justify-between text-xs text-slate-100 gap-6">
                        <div className="flex items-start gap-3">
                            <div className="flex flex-col leading-tight">
                                <span className="font-medium">{`Documents found`}</span>
                                <span className="text-slate-200">{documentsFoundCount}/{totalCount}</span>
                            </div>
                            <div className="h-1.5 w-20 rounded-full bg-white/25 overflow-hidden mt-0.5">
                                <div
                                    className="h-full bg-persian-300"
                                    style={{ width: `${docsProgress}%` }}
                                />
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="h-1.5 w-20 rounded-full bg-white/25 overflow-hidden mt-0.5">
                                <div
                                    className="h-full bg-emerald-300"
                                    style={{ width: `${answeredProgress}%` }}
                                />
                            </div>
                            <div className="flex flex-col leading-tight">
                                <span className="font-medium">{`Completed`}</span>
                                <span className="text-slate-200">{answeredCount}/{totalCount}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
                {allQuestions.length === 0 ? (
                    <div className="p-6 text-center">
                        <p className="text-sm text-gray-500">No information requests</p>
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {sections.map((section) => (
                            <div key={section.id}>
                                <div className="sticky top-0 z-10 -mt-1 mb-2 bg-white py-2">
                                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                                        {section.name}
                                    </h3>
                                </div>
                                <div className="space-y-2">
                                    {section.questions.map((question) => (
                                        <Link
                                            key={question.id}
                                            to={`/deal/${dealId}/questions/${question.id}`}
                                            className="group block"
                                        >
                                            <div className="flex items-center justify-between py-3 px-4 rounded-lg border border-gray-100 bg-white hover:bg-gray-50">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <QuestionStateDot state={getSellerRequestQuestionState(question)} />
                                                    <p className="text-sm text-gray-800 font-medium truncate">{question.title}</p>
                                                </div>
                                                <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform group-hover:translate-x-0.5" />
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function QuestionStateDot(props: { state: SellerRequestQuestionState }) {
    const classes: Record<SellerRequestQuestionState, string> = {
        no_answer: "bg-gray-300",
        documents_found: "bg-persian-300",
        answered: "bg-emerald-500",
    };

    return (
        <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${classes[props.state]}`} />
    );
}

// ============================================================================
// Information Required Section
// ============================================================================

type TaskData = DealTasksQuery["tasks"][0];
type DisplayTaskItem = {
    id: string;
    title: string;
    status: TaskStatus;
    priority?: TaskPriority | null;
    subTasks?: Pick<TaskData["subTasks"][0], "id" | "title" | "status" | "priority" | "assignedTo">[];
    assignee?: Pick<TaskData["assignedTo"][0], "id" | "name" | "avatar">;
    assignedTo?: Pick<TaskData["assignedTo"][0], "id">[];
    parentTitle?: string;
};

function isTaskCompleted(task: Pick<TaskData, "status">): boolean {
    return task.status === TaskStatus.Completed;
}

function isTaskAssignedToUser(task: { assignedTo?: Pick<TaskData["assignedTo"][0], "id">[] }, accountId?: string): boolean {
    if (!accountId) {
        return false;
    }
    return (task.assignedTo ?? []).some((assignee) => assignee.id === accountId);
}

function dedupeTasksById(tasks: TaskData[]): TaskData[] {
    const seen = new Set<string>();
    return tasks.filter((task) => {
        if (seen.has(task.id)) {
            return false;
        }
        seen.add(task.id);
        return true;
    });
}

function sortTasksByCompletion(tasks: TaskData[]): TaskData[] {
    return [...tasks].sort((a, b) => Number(isTaskCompleted(a)) - Number(isTaskCompleted(b)));
}

function dedupeDisplayTasksById(tasks: DisplayTaskItem[]): DisplayTaskItem[] {
    const seen = new Set<string>();
    return tasks.filter((task) => {
        if (seen.has(task.id)) {
            return false;
        }
        seen.add(task.id);
        return true;
    });
}

function sortDisplayTasksByCompletion(tasks: DisplayTaskItem[]): DisplayTaskItem[] {
    return [...tasks].sort((a, b) => Number(isTaskCompleted(a)) - Number(isTaskCompleted(b)));
}

function TaskRow(props: {
    task: DisplayTaskItem;
    dealId: string;
    isUrgent?: boolean;
    isSubTask?: boolean;
    isLast?: boolean;
}) {
    const { task, dealId, isUrgent, isSubTask } = props;
    const completed = isTaskCompleted(task);

    return (
        <Link
            to={`/deal/${dealId}/tasks/${task.id}`}
            className="group block"
        >
            <div className={classNames(
                "flex items-center justify-between px-4 transition-all duration-150",
                isSubTask ? "py-2 rounded-none pl-8" : "py-3 ",
                props.isLast ? "rounded-b-lg" : "",
                props.task.subTasks && props.task.subTasks.length > 0 && !isUrgent ? "rounded-t-lg" : "rounded-lg",
                isTaskCompleted(task)
                    ? "bg-white hover:bg-gray-50"
                    : isUrgent
                        ? "bg-gradient-to-r from-red-50 to-orange-50 hover:from-red-100 hover:to-orange-100"
                        : "bg-white hover:bg-indigo-50 ",
            )}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    {completed ? (
                        <div className="w-4 h-4 rounded-full bg-persian-500 flex items-center justify-center flex-shrink-0">
                            <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} />
                        </div>
                    ) : isSubTask ? (
                        <CircleIcon className="w-4 h-4 text-gray-300 flex-shrink-0" />
                    ) : (
                        <TaskPriorityIcon priority={task.priority ?? TaskPriority.None} />
                    )}
                    <p className={classNames(
                        "text-sm truncate",
                        completed ? "text-gray-400 line-through" : "text-gray-800 font-medium"
                    )}>
                        {task.title}
                    </p>
                    {task.parentTitle ? (
                        <span className="text-xs text-gray-400 truncate">
                            subtask of {task.parentTitle}
                        </span>
                    ) : null}
                </div>
                {!isSubTask && task.assignee ? (
                    <div className="flex items-center gap-2 mr-2 min-w-0 max-w-44">
                        <Avatar
                            account={task.assignee}
                            size="xs"
                            withBorder={false}
                        />
                        <span className="text-xs text-gray-600 truncate">{task.assignee.name}</span>
                    </div>
                ) : null}
                {isSubTask && task.assignee ? (
                    <div className="mr-2 flex-shrink-0">
                        <Avatar
                            account={task.assignee}
                            size="xs"
                            withBorder={false}
                        />
                    </div>
                ) : null}
                {!isSubTask ? <SubtaskProgress subTasks={task.subTasks} /> : null}
                <ChevronRightIcon className={classNames(
                    "w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5",
                    completed ? "text-gray-300" : "text-gray-400"
                )} />
            </div>
        </Link>
    );
}

function SubtaskProgress(props: {
    subTasks?: Array<{ status: TaskStatus }>;
}) {
    const { subTasks } = props;

    if (!subTasks || subTasks.length === 0) {
        return null;
    }

    const completed = subTasks.filter((task) => task.status === TaskStatus.Completed).length;
    const total = subTasks.length;
    const ratio = total === 0 ? 0 : completed / total;
    const radius = 5;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - ratio * circumference;

    return (
        <div className="flex items-center gap-x-1 text-xs text-gray-500 mr-2 flex-shrink-0">
            <span>{completed}/{total}</span>
            <svg width="12" height="12" className="text-gray-300">
                <circle
                    cx="6"
                    cy="6"
                    r={radius}
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                />
                <circle
                    cx="6"
                    cy="6"
                    r={radius}
                    stroke="#4f46e5"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform="rotate(-90 6 6)"
                />
            </svg>
        </div>
    );
}

// Circular progress component
function CircularProgress({ progress, size = 60 }: { progress: number; size?: number }) {
    const strokeWidth = 6;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg className="transform -rotate-90" width={size} height={size}>
                <circle
                    className="text-white/20"
                    strokeWidth={strokeWidth}
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
                <circle
                    className="text-white transition-all duration-500 ease-out"
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-white">{Math.round(progress)}%</span>
            </div>
        </div>
    );
}

function InformationRequiredSection(props: {
    dealId: string;
    tasks: TaskData[];
    isTasksLoading: boolean;
    currentAccountId?: string;
}) {
    const { tasks, dealId, isTasksLoading, currentAccountId } = props;

    if (isTasksLoading) {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <SparklesIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading tasks...</h3>
                    <p className="text-gray-500 text-sm max-w-xs">
                        Fetching your deal tasks
                    </p>
                </div>
            </div>
        );
    }

    if (tasks.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 p-8">
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                        <SparklesIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No requests yet</h3>
                    <p className="text-gray-500 text-sm max-w-xs">
                        Information requests from buyers will appear here
                    </p>
                </div>
            </div>
        );
    }

    const urgentTopLevelTasks = dedupeTasksById(tasks.filter(
        (task) => task.priority === TaskPriority.Urgent
    ));
    const urgentSubTasks: DisplayTaskItem[] = tasks.flatMap((task) =>
        task.subTasks
            .filter((subTask) => subTask.priority === TaskPriority.Urgent)
            .map((subTask) => ({
                id: subTask.id,
                title: subTask.title,
                status: subTask.status,
                priority: subTask.priority,
                parentTitle: task.title,
            })),
    );
    const assignedTasks = dedupeTasksById(tasks.filter(
        (task) => isTaskAssignedToUser(task, currentAccountId)
    ));
    const remainingTasks = dedupeTasksById(tasks.filter(
        (task) => task.priority !== TaskPriority.Urgent
            && !isTaskAssignedToUser(task, currentAccountId),
    ));

    const sortedUrgentTasks = sortDisplayTasksByCompletion(dedupeDisplayTasksById([
        ...urgentTopLevelTasks.map((task) => ({
            id: task.id,
            title: task.title,
            status: task.status,
            priority: task.priority,
            subTasks: task.subTasks,
            assignee: task.assignedTo[0],
            assignedTo: task.assignedTo,
        })),
        ...urgentSubTasks,
    ]));
    const sortedAssignedTasks = sortTasksByCompletion(assignedTasks);
    const sortedRemainingTasks = sortTasksByCompletion(remainingTasks);

    const allTasksForProgress = dedupeDisplayTasksById([
        ...tasks.map((task) => ({
            id: task.id,
            title: task.title,
            status: task.status,
            priority: task.priority,
        })),
        ...tasks.flatMap((task) =>
            task.subTasks.map((subTask) => ({
                id: subTask.id,
                title: subTask.title,
                status: subTask.status,
                priority: subTask.priority,
                parentTitle: task.title,
            })),
        ),
    ]);
    const completedCount = allTasksForProgress.filter(isTaskCompleted).length;
    const remainingCount = allTasksForProgress.length - completedCount;
    const progress = allTasksForProgress.length === 0
        ? 0
        : (completedCount / allTasksForProgress.length) * 100;
    const allAnswered = allTasksForProgress.length > 0 && completedCount === allTasksForProgress.length;

    const sections = [
        {
            key: "assigned",
            title: "Assigned to me",
            tasks: sortedAssignedTasks.map((task) => ({
                id: task.id,
                title: task.title,
                status: task.status,
                priority: task.priority,
                subTasks: task.subTasks,
                assignee: task.assignedTo[0],
                assignedTo: task.assignedTo,
            })),
            urgent: false,
        },
        {
            key: "urgent",
            title: "Urgent",
            tasks: sortedUrgentTasks,
            urgent: true,
        },

        {
            key: "remaining",
            title: "Other tasks",
            tasks: sortedRemainingTasks.map((task) => ({
                id: task.id,
                title: task.title,
                status: task.status,
                priority: task.priority,
                subTasks: task.subTasks,
                assignee: task.assignedTo[0],
                assignedTo: task.assignedTo,
            })),
            urgent: false,
        },
    ].filter((section) => section.tasks.length > 0);

    const shouldShowSubTasks = (task: DisplayTaskItem) =>
        isTaskAssignedToUser(task, currentAccountId) && (task.subTasks?.length ?? 0) > 0;

    const sortedSubTasks = (task: DisplayTaskItem): DisplayTaskItem[] =>
        (task.subTasks ?? [])
            .filter((subTask) => subTask.priority !== TaskPriority.Urgent)
            .sort(
                (a, b) => Number(a.status === TaskStatus.Completed) - Number(b.status === TaskStatus.Completed)
            )
            .map((subTask) => ({
                id: subTask.id,
                title: subTask.title,
                status: subTask.status,
                priority: subTask.priority,
                assignee: subTask.assignedTo[0],
                parentTitle: task.title,
            }));

    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-persian-990 to-persian-950 px-5 py-4 text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-base font-semibold">Seller Tasks</h2>
                        <p className="text-xs text-indigo-100 font-medium mt-0.5">
                            {`${completedCount}/${allTasksForProgress.length} completed`}
                        </p>
                    </div>
                    <CircularProgress progress={progress} />
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto scrollbar-thin">
                {allAnswered ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                            <CheckIcon className="w-10 h-10 text-emerald-600" strokeWidth={2.5} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">All Complete!</h3>
                        <p className="text-gray-500">You've completed all information tasks</p>
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {sections.map((section) => (
                            <div key={section.key}>
                                <div className="flex items-center gap-2 mb-3">
                                    {section.urgent ? (
                                        <ZapIcon className="w-4 h-4 text-red-500" />
                                    ) : null}
                                    <h3 className={classNames(
                                        "text-xs font-bold uppercase tracking-wider",
                                        section.urgent ? "text-red-600" : "text-gray-500"
                                    )}>
                                        {section.title}
                                    </h3>
                                </div>
                                <div className="space-y-2">
                                    {section.tasks.map((task) => (
                                        <div key={task.id} className={classNames(
                                            "border rounded-lg",
                                            isTaskCompleted(task)
                                                ? "bg-white hover:bg-gray-50"
                                                : section.urgent
                                                    ? " border-red-100"
                                                    : " hover:border-indigo-200",
                                        )}>
                                            <TaskRow
                                                task={task}
                                                dealId={dealId}
                                                isUrgent={section.urgent}
                                            />
                                            {section.key !== "urgent" && shouldShowSubTasks(task) ? (
                                                <div className="">
                                                    {sortedSubTasks(task).map((subTask, i) => (
                                                        <TaskRow
                                                            key={subTask.id}
                                                            task={subTask}
                                                            dealId={dealId}
                                                            isSubTask
                                                            isLast={i === sortedSubTasks(task).length - 1}
                                                        />
                                                    ))}
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// Needs Attention Section
// ============================================================================

function NeedsAttentionSection() {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                    <BellRingIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                    <h3 className="font-semibold text-gray-900">Needs Attention</h3>
                    <p className="text-xs text-gray-500">0 items</p>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <p className="text-sm text-gray-500">
                    Documents and buyer questions requiring your review will appear here
                </p>
            </div>
        </div>
    );
}

// ============================================================================
// Buyers Section
// ============================================================================

type BuyerData = SellerDashboardQuery["deal"]["buyers"][0];
type DealStageData = SellerDashboardQuery["deal"]["dealStages"][0];

function BuyersSection(props: {
    dealId: string;
    buyers: BuyerData[];
    dealStages: DealStageData[];
    isActive: boolean;
}) {
    const { buyers, dealStages, isActive, dealId } = props;

    return (
        <div
            className={classNames(
                "bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-300",
                isActive ? "opacity-100" : "opacity-50"
            )}
        >
            {/* Header */}
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <UsersIcon className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900">Buyers</h3>
                        <p className="text-xs text-gray-500">
                            {buyers.length} active
                        </p>
                    </div>
                </div>
                {!isActive && (
                    <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                        Coming soon
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="max-h-64 overflow-y-auto">
                {buyers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                            <UsersIcon className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500">
                            {isActive
                                ? "No buyers added yet"
                                : "Buyers will appear here once you reach the Finding Buyers stage"}
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {buyers.map((buyer) => {
                            const dealAccounts = buyer.dealFirms.flatMap(
                                (firm) => firm.dealAccounts
                            );

                            return (
                                <NavLink
                                    key={buyer.id}
                                    to={`/deal/${dealId}/buyers/${buyer.id}`}
                                    className="block hover:bg-gray-50 transition-colors"
                                >
                                    <div className="px-5 py-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="font-medium text-gray-900 truncate">
                                                {buyer.name}
                                            </p>
                                            <div className="flex -space-x-1.5">
                                                {dealAccounts.slice(0, 3).map((da) => (
                                                    <Avatar
                                                        key={da.account.id}
                                                        account={da.account}
                                                        size="s"
                                                    />
                                                ))}
                                                {dealAccounts.length > 3 && (
                                                    <span className="flex items-center justify-center w-6 h-6 text-xs font-medium bg-gray-200 text-gray-600 rounded-full ring-2 ring-white">
                                                        +{dealAccounts.length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <DealProgress
                                            mode="compact"
                                            dealStages={dealStages}
                                            currentDealStage={buyer.currentDealStage}
                                            currentDealStageStatus={buyer.currentDealStageStatus}
                                        />
                                    </div>
                                </NavLink>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

